/**
 * Pre-defined language list with ISO 639-1 codes.
 * Used for structured language selection on candidate profiles.
 */
export const LANGUAGES = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "nl", label: "Nederlands" },
  { value: "pl", label: "Polski" },
  { value: "cs", label: "Čeština" },
  { value: "da", label: "Dansk" },
  { value: "sv", label: "Svenska" },
  { value: "no", label: "Norsk" },
  { value: "fi", label: "Suomi" },
  { value: "hu", label: "Magyar" },
  { value: "ro", label: "Română" },
  { value: "bg", label: "Български" },
  { value: "hr", label: "Hrvatski" },
  { value: "sk", label: "Slovenčina" },
  { value: "sl", label: "Slovenščina" },
  { value: "el", label: "Ελληνικά" },
  { value: "tr", label: "Türkçe" },
  { value: "ru", label: "Русский" },
  { value: "uk", label: "Українська" },
  { value: "zh", label: "中文" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "ar", label: "العربية" },
  { value: "hi", label: "हिन्दी" },
  { value: "th", label: "ไทย" },
  { value: "vi", label: "Tiếng Việt" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "he", label: "עברית" },
  { value: "fa", label: "فارسی" },
];

const codeToLabel = Object.fromEntries(LANGUAGES.map((l) => [l.value, l.label]));

/** English display names for language codes */
const codeToEnglish = {
  de: "German", en: "English", fr: "French", es: "Spanish", it: "Italian",
  pt: "Portuguese", nl: "Dutch", pl: "Polish", cs: "Czech", da: "Danish",
  sv: "Swedish", no: "Norwegian", fi: "Finnish", hu: "Hungarian", ro: "Romanian",
  bg: "Bulgarian", hr: "Croatian", sk: "Slovak", sl: "Slovenian", el: "Greek",
  tr: "Turkish", ru: "Russian", uk: "Ukrainian", zh: "Chinese", ja: "Japanese",
  ko: "Korean", ar: "Arabic", hi: "Hindi", th: "Thai", vi: "Vietnamese",
  id: "Indonesian", ms: "Malay", he: "Hebrew", fa: "Persian",
};

/** German display names for language codes */
const codeToGerman = {
  de: "Deutsch", en: "Englisch", fr: "Französisch", es: "Spanisch", it: "Italienisch",
  pt: "Portugiesisch", nl: "Niederländisch", pl: "Polnisch", cs: "Tschechisch", da: "Dänisch",
  sv: "Schwedisch", no: "Norwegisch", fi: "Finnisch", hu: "Ungarisch", ro: "Rumänisch",
  bg: "Bulgarisch", hr: "Kroatisch", sk: "Slowakisch", sl: "Slowenisch", el: "Griechisch",
  tr: "Türkisch", ru: "Russisch", uk: "Ukrainisch", zh: "Chinesisch", ja: "Japanisch",
  ko: "Koreanisch", ar: "Arabisch", hi: "Hindi", th: "Thai", vi: "Vietnamesisch",
  id: "Indonesisch", ms: "Malaiisch", he: "Hebräisch", fa: "Persisch",
};

/** Map of common display names (EN/DE) to ISO codes for legacy data migration */
const nameToCode = {
  german: "de", deutsch: "de",
  english: "en", englisch: "en",
  french: "fr", französisch: "fr", francais: "fr",
  spanish: "es", spanisch: "es",
  italian: "it", italienisch: "it",
  portuguese: "pt", portugiesisch: "pt",
  dutch: "nl", niederländisch: "nl", holländisch: "nl",
  polish: "pl", polnisch: "pl",
  czech: "cs", tschechisch: "cs",
  danish: "da", dänisch: "da",
  swedish: "sv", schwedisch: "sv",
  norwegian: "no", norwegisch: "no",
  finnish: "fi", finnisch: "fi",
  hungarian: "hu", ungarisch: "hu",
  romanian: "ro", rumänisch: "ro",
  bulgarian: "bg", bulgarisch: "bg",
  croatian: "hr", kroatisch: "hr",
  slovak: "sk", slowakisch: "sk",
  slovenian: "sl", slowenisch: "sl",
  greek: "el", griechisch: "el",
  turkish: "tr", türkisch: "tr",
  russian: "ru", russisch: "ru",
  ukrainian: "uk", ukrainisch: "uk",
  chinese: "zh", chinesisch: "zh",
  japanese: "ja", japanisch: "ja",
  korean: "ko", koreanisch: "ko",
  arabic: "ar", arabisch: "ar",
  hindi: "hi",
  thai: "th",
  vietnamese: "vi", vietnamesisch: "vi",
  indonesian: "id", indonesisch: "id",
  malay: "ms", malaiisch: "ms",
  hebrew: "he", hebräisch: "he",
  persian: "fa", persisch: "fa", farsi: "fa",
};

/**
 * Normalize a language value to its ISO code. Handles:
 * - Already an ISO code ("de") → "de"
 * - Display name in EN/DE ("German", "Deutsch") → "de"
 * - Unknown → returns as-is (fallback)
 */
export function toIsoCode(lang) {
  if (!lang) return lang;
  const lower = lang.toLowerCase().trim();
  if (codeToLabel[lower]) return lower;
  return nameToCode[lower] ?? lang;
}

/**
 * Get display label for a language value (native name). Handles ISO codes and legacy names.
 */
export function languageLabel(lang) {
  if (!lang) return lang;
  const code = toIsoCode(lang);
  return codeToLabel[code] ?? lang;
}

/**
 * Get bilingual display label: "German (Deutsch)" for EN UI, "Deutsch (German)" for DE UI.
 * Falls back to native name if code is unknown.
 */
export function languageDisplayLabel(lang, uiLang = "de") {
  if (!lang) return lang;
  const code = toIsoCode(lang);
  const de = codeToGerman[code];
  const en = codeToEnglish[code];
  if (!de || !en) return codeToLabel[code] ?? lang;
  return uiLang === "de" ? `${de} (${en})` : `${en} (${de})`;
}
